import { css, html, LitElement, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
@safeCustomElement('mdc-post-card')
export class MdcPostCard extends LitElement {
  @property({ type: String, attribute: 'background-image-src' }) backgroundImageSrc = ''
  @property({ type: String, attribute: 'background-image-alt' }) backgroundImageAlt = ''
  @property({ type: String, attribute: 'user-avatar-image-src' }) userAvatarImageSrc = ''
  @property({ type: String, attribute: 'user-avatar-image-alt' }) userAvatarImageAlt = ''
  @property({ type: String, attribute: 'user-full-name' }) userFullName = ''
  @property({ type: String, attribute: 'user-link' }) userLink = ''
  @property({ type: String, attribute: 'brand-avatar-image-src' }) brandAvatarImageSrc = ''
  @property({ type: String, attribute: 'brand-avatar-image-alt' }) brandAvatarImageAlt = ''
  @property({ type: String, attribute: 'brand-link' }) brandLink = ''
  @property({ type: String, attribute: 'post-link' }) postLink = ''

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
			--mdc-post-card-name-font-family: var(--typography-font-family-sans);
			--mdc-post-card-name-color: var(--color-functional-gray-white, #ffffff);
			--mdc-post-card-header-gradient: linear-gradient(
				180deg,
				rgba(var(--color-functional-gray-black-rgb), 0.5) 0%,
				rgba(var(--color-functional-gray-black-rgb), 0) 100%
			);
			--mdc-post-card-view-post-background: var(--color-functional-gray-white, #ffffff);
			--mdc-post-card-view-post-shadow: 0 0 12px 0 rgba(var(--color-functional-gray-black-rgb), 0.12);
		}

		.mdc-post-card {
			--_avatar-size: 2rem;
			--_inner-horizontal-padding: 0.75rem;
			--_inner-vertical-padding: 0.5rem;

			position: relative;
			overflow: hidden;
			border: 1px solid var(--color-functional-gray-200, #e5e5e5);
			display: grid;
		}

		.mdc-post-card *,
		.mdc-post-card *::before,
		.mdc-post-card *::after {
			box-sizing: border-box;
		}

		.mdc-post-card a {
			display: flex;
			color: inherit;
			text-decoration: none;
		}

		.mdc-post-card__content,
		.mdc-post-card,
		.mdc-post-card__background-image {
			max-width: 100%;
			height: auto;
			border-radius: 0.5rem;
			aspect-ratio: 1;
		}

		.mdc-post-card__background-image {
			object-fit: cover;
			grid-column: 1;
			grid-row: 1;
		}

		.mdc-post-card__content {
			display: flex;
			flex-direction: column;
			position: absolute;
			z-index: 1;
			inset: 0;
			grid-column: 1;
			grid-row: 1;
		}

		.mdc-post-card__content > * {
			flex-shrink: 0;
		}

		.mdc-post-card__avatar {
			flex-shrink: 0;
			width: var(--_avatar-size);
			height: var(--_avatar-size);
			border: 1px solid var(--color-functional-gray-200, #e5e5e5);
			border-radius: 100%;
		}

		.mdc-post-card__user-full-name {
			max-width: 13ch;
			overflow: hidden;
			font-family: var(--mdc-post-card-name-font-family);
			font-size: 0.75rem;
			color: var(--mdc-post-card-name-color);
			font-weight: 450;
			line-height: 1.3;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.mdc-post-card__user-content {
			display: flex;
			align-items: center;
			gap: 0.5rem;
		}

		.mdc-post-card__header {
			display: flex;
			align-items: center;
			padding: var(--_inner-vertical-padding) var(--_inner-horizontal-padding);
			background: var(--mdc-post-card-header-gradient);
			gap: 0.5rem;
		}

		.mdc-post-card__header-right {
			margin-left: auto;
		}

		.mdc-post-card__footer {
			margin-top: auto;
			margin-left: auto;
			padding: var(--_inner-horizontal-padding);
		}

		.mdc-post-card__view-post {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 2.5rem;
			height: 2.5rem;
			border-radius: 5rem;
			background: var(--mdc-post-card-view-post-background);
			box-shadow: var(--mdc-post-card-view-post-shadow);
		}

		@media (hover: hover) {
			.mdc-post-card__view-post {
				opacity: 0;
				transition: opacity 0.2s;
				pointer-events: none;
			}

			.mdc-post-card:hover .mdc-post-card__view-post {
				opacity: 1;
				pointer-events: auto;
			}
		}
	`

  render() {
    const hasContent =
      this.userAvatarImageSrc || this.userFullName || this.brandAvatarImageSrc || this.postLink

    const userContent =
      this.userAvatarImageSrc || this.userFullName
        ? html`
						<div class="mdc-post-card__user-content">
							${
                this.userAvatarImageSrc
                  ? this.userLink
                    ? html`
											<a href=${this.userLink} target="_blank" rel="noopener noreferrer">
												<img class="mdc-post-card__avatar" src=${this.userAvatarImageSrc} alt=${this.userAvatarImageAlt || ''} />
											</a>
										`
                    : html`<img class="mdc-post-card__avatar" src=${this.userAvatarImageSrc} alt=${this.userAvatarImageAlt || ''} />`
                  : nothing
              }
							${
                this.userFullName
                  ? this.userLink
                    ? html`
											<a href=${this.userLink} target="_blank" rel="noopener noreferrer">
												<span class="mdc-post-card__user-full-name">${this.userFullName}</span>
											</a>
										`
                    : html`<span class="mdc-post-card__user-full-name">${this.userFullName}</span>`
                  : nothing
              }
						</div>
					`
        : nothing

    const brandContent = this.brandAvatarImageSrc
      ? html`
					<div class="mdc-post-card__brand-content">
						${
              this.brandLink
                ? html`
									<a href=${this.brandLink} target="_blank" rel="noopener noreferrer">
										<img class="mdc-post-card__avatar" src=${this.brandAvatarImageSrc} alt=${this.brandAvatarImageAlt || ''} />
									</a>
								`
                : html`<img class="mdc-post-card__avatar" src=${this.brandAvatarImageSrc} alt=${this.brandAvatarImageAlt || ''} />`
            }
					</div>
				`
      : nothing

    return html`
			<div class="mdc-post-card">
				<img class="mdc-post-card__background-image" src=${this.backgroundImageSrc} alt=${this.backgroundImageAlt} />
				${
          hasContent
            ? html`
							<div class="mdc-post-card__content">
								${
                  userContent || brandContent
                    ? html`
											<header class="mdc-post-card__header">
												${userContent ? html`<div class="mdc-post-card__header-left">${userContent}</div>` : nothing}
												${brandContent ? html`<div class="mdc-post-card__header-right">${brandContent}</div>` : nothing}
											</header>
										`
                    : nothing
                }
								${
                  this.postLink
                    ? html`
											<footer class="mdc-post-card__footer">
												<a href=${this.postLink} target="_blank" rel="noopener noreferrer" class="mdc-post-card__view-post" aria-label="View post">
													<mdc-icon name="arrow-right"></mdc-icon>
												</a>
											</footer>
										`
                    : nothing
                }
							</div>
						`
            : nothing
        }
			</div>
		`
  }
}
